import { useMemo } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AlertTriangle, XCircle, Info, CheckCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ActivityWithExchanges } from '@/lib/types/project-graph'

interface ValidationIssue {
  id: string
  type: 'error' | 'warning' | 'info'
  message: string
  location: string
  activityId?: string
  exchangeId?: string
}

interface ValidationPanelProps {
  activities: ActivityWithExchanges[]
  onSelectNode?: (nodeId: string, nodeType: 'activity' | 'exchange') => void
}

function computeValidationIssues(activities: ActivityWithExchanges[]): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  let counter = 0

  for (const activity of activities) {
    const exchanges = activity.exchanges ?? []

    // Check: no production exchange (missing reference product)
    const hasProduction = exchanges.some((e) => e.exchange_type === 'production')
    if (!hasProduction) {
      issues.push({
        id: String(++counter),
        type: 'error',
        message: 'No reference product (production exchange)',
        location: activity.name,
        activityId: activity.id,
      })
    }

    // Check: exchanges with zero amount
    for (const ex of exchanges) {
      if (ex.amount === 0) {
        issues.push({
          id: String(++counter),
          type: 'warning',
          message: `Exchange amount is zero: ${ex.name}`,
          location: `${activity.name} > ${ex.name}`,
          activityId: activity.id,
          exchangeId: ex.id,
        })
      }
    }

    // Check: activity has no exchanges at all (orphan activity).
    // An activity with only a production exchange (and optional biosphere
    // outputs) is a legitimate functional unit / pure flow / aggregated
    // reference activity — having no technosphere inputs is NOT an issue.
    if (exchanges.length === 0) {
      issues.push({
        id: String(++counter),
        type: 'info',
        message: 'No exchanges (orphan activity)',
        location: activity.name,
        activityId: activity.id,
      })
    }
  }

  return issues
}

export function ValidationPanel({ activities, onSelectNode }: ValidationPanelProps) {
  // Validation is pure over `activities` and recomputes whenever the
  // project graph changes — no manual refresh needed.
  const issues = useMemo(() => computeValidationIssues(activities), [activities])

  const errorCount = issues.filter((i) => i.type === 'error').length
  const warningCount = issues.filter((i) => i.type === 'warning').length

  return (
    <div className="h-full flex flex-col">
      {/* Summary bar */}
      <div className="flex items-center justify-between px-2 py-1 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3 text-xs">
          {issues.length === 0 ? (
            <div className="flex items-center gap-1.5 text-green-600">
              <CheckCircle className="h-3.5 w-3.5" />
              <span>No issues</span>
            </div>
          ) : (
            <>
              {errorCount > 0 && (
                <div className="flex items-center gap-1 text-red-600">
                  <XCircle className="h-3.5 w-3.5" />
                  <span>
                    {errorCount} error{errorCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {warningCount > 0 && (
                <div className="flex items-center gap-1 text-yellow-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>
                    {warningCount} warning{warningCount !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Issues list */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {issues.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 opacity-30 text-green-500" />
              <p className="text-xs">All validations passed</p>
            </div>
          ) : (
            issues.map((issue) => (
              <ValidationIssueRow key={issue.id} issue={issue} onSelectNode={onSelectNode} />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

interface ValidationIssueRowProps {
  issue: ValidationIssue
  onSelectNode?: (nodeId: string, nodeType: 'activity' | 'exchange') => void
}

function ValidationIssueRow({ issue, onSelectNode }: ValidationIssueRowProps) {
  const iconMap = {
    error: <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />,
    warning: <AlertTriangle className="h-3.5 w-3.5 text-yellow-500 shrink-0" />,
    info: <Info className="h-3.5 w-3.5 text-blue-500 shrink-0" />,
  }

  const bgMap = {
    error: 'hover:bg-red-50 dark:hover:bg-red-950/20',
    warning: 'hover:bg-yellow-50 dark:hover:bg-yellow-950/20',
    info: 'hover:bg-blue-50 dark:hover:bg-blue-950/20',
  }

  const handleClick = () => {
    if (issue.activityId && onSelectNode) {
      onSelectNode(issue.activityId, 'activity')
    }
  }

  return (
    <div
      className={cn(
        'flex items-start gap-2 p-2 rounded-md cursor-pointer transition-colors',
        bgMap[issue.type],
      )}
      onClick={handleClick}
    >
      {iconMap[issue.type]}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium">{issue.message}</p>
        <p className="text-[10px] text-muted-foreground truncate">{issue.location}</p>
      </div>
    </div>
  )
}
